import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateContainment } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function ContainmentModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:containment:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { items: [] })
  const initial = superseding ? { items: superseding.engineering_inputs?.items || [] } : draft
  const [items, setItemsRaw] = useState(initial.items)
  const setItems = (u) => setItemsRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, items: n })); return n })

  function addItem() { setItems((p) => [...p, { id: `C${Date.now()}`, label: '', type: 'tray', length: '', width: '', height: '', diameter: '', fittings: [] }]) }
  function updateItem(id, patch) { setItems((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x))) }
  function removeItem(id) { setItems((p) => p.filter((x) => x.id !== id)) }
  function addFitting(cid) { setItems((p) => p.map((x) => (x.id === cid ? { ...x, fittings: [...x.fittings, { id: `F${Date.now()}`, type: '', count: '' }] } : x))) }
  function updateFitting(cid, fid, patch) { setItems((p) => p.map((x) => (x.id === cid ? { ...x, fittings: x.fittings.map((f) => (f.id === fid ? { ...f, ...patch } : f)) } : x))) }
  function removeFitting(cid, fid) { setItems((p) => p.map((x) => (x.id === cid ? { ...x, fittings: x.fittings.filter((f) => f.id !== fid) } : x))) }

  const itemsForCalc = useMemo(() => items.map((c) => ({
    label: c.label, type: c.type, length: c.length === '' ? NaN : Number(c.length),
    width: c.type === 'tray' ? (c.width === '' ? NaN : Number(c.width)) : undefined,
    height: c.type === 'tray' ? (c.height === '' ? NaN : Number(c.height)) : undefined,
    diameter: c.type === 'conduit' ? (c.diameter === '' ? NaN : Number(c.diameter)) : undefined,
    fittings: c.fittings.map((f) => ({ type: f.type, count: Number(f.count) })),
  })), [items])

  const result = useMemo(() => {
    if (itemsForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateContainment(itemsForCalc), error: null } } catch (e) { return { value: null, error: e.message } }
  }, [itemsForCalc])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { items }
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: 'm', geometrySnapshot: { items: [] }, engineeringInputs, breakdownSnapshot: { steps: result.value.steps, itemBreakdown: result.value.itemBreakdown, secondaryOutputs: result.value.secondaryOutputs }, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Containment</label>
        <Button size="sm" variant="secondary" onClick={addItem}>+ Add Item</Button>
      </div>
      {items.map((c) => (
        <div key={c.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Label" value={c.label} onChange={(e) => updateItem(c.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeItem(c.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6, marginBottom: 6 }}>
            <L label="Type"><Select value={c.type} onChange={(e) => updateItem(c.id, { type: e.target.value })} style={{ fontSize: 11, width: '100%' }}><option value="tray">Cable Tray</option><option value="conduit">Conduit</option></Select></L>
            <L label="Length (m)"><Input type="number" value={c.length} onChange={(e) => updateItem(c.id, { length: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          </div>
          {c.type === 'tray' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
              <L label="Width (m, configuration only)"><Input type="number" value={c.width} onChange={(e) => updateItem(c.id, { width: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
              <L label="Height (m, configuration only)"><Input type="number" value={c.height} onChange={(e) => updateItem(c.id, { height: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            </div>
          ) : (
            <L label="Diameter (m, configuration only)"><Input type="number" value={c.diameter} onChange={(e) => updateItem(c.id, { diameter: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 4px' }}>
            <span style={{ fontSize: 11, fontWeight: 700 }}>Fittings</span>
            <Button size="sm" variant="secondary" onClick={() => addFitting(c.id)}>+ Add Fitting</Button>
          </div>
          {c.fittings.map((f) => (
            <div key={f.id} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <Input placeholder="Type" value={f.type} onChange={(e) => updateFitting(c.id, f.id, { type: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
              <Input type="number" placeholder="Count" value={f.count} onChange={(e) => updateFitting(c.id, f.id, { count: e.target.value })} style={{ width: 80, fontSize: 11 }} />
              <button onClick={() => removeFitting(c.id, f.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0', padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>
          Total: {formatNumber(result.value.finalQuantity)} m
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>{children}</div> }
